import { Badge } from "@/components/ui/badge"
import { DEFAULT_CATEGORY_COLOR } from "@/constants/KnowledgeBaseConstant"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  DocumentTagCategoryGroup,
  DocumentTagSource,
  KnowledgeBaseTag,
} from "@/types/knowledgebaseCategories"
import { classNames } from "@/utils/classNames"
import { getLocalizedLabel } from "@/utils/localizedLabel"
import { Check, Loader2, X } from "lucide-react"
import * as React from "react"

export interface DocumentTagMenuTag {
  id: string
  tagId: string
  tag: KnowledgeBaseTag
  source: DocumentTagSource
  approvedAt: string | null
  categoryColor: string
}

export interface DocumentTagMenuCategory {
  categoryId: string
  categoryDisplayNameEn: string
  categoryColor: string
  tags: DocumentTagMenuTag[]
}

export interface DocumentTagEntry {
  category: DocumentTagMenuCategory
  tag: DocumentTagMenuTag
}

const isUnapprovedAutoTag = (
  source: DocumentTagSource,
  approvedAt: string | null,
) => source === "auto" && approvedAt === null

const getDocumentTagBadgeVariant = (
  source: DocumentTagSource,
  approvedAt: string | null,
) => {
  return isUnapprovedAutoTag(source, approvedAt) ? "outline" : "secondary"
}

const getDocumentTagBadgeClassName = (
  source: DocumentTagSource,
  approvedAt: string | null,
) => {
  return classNames(
    "px-1.5 py-0 text-[10px] font-medium leading-4 text-grey6",
    isUnapprovedAutoTag(source, approvedAt) ? "border-dashed" : "",
  )
}

const getDocumentTagBadgeStyle = (
  source: DocumentTagSource,
  approvedAt: string | null,
  categoryColor: string,
) => {
  return isUnapprovedAutoTag(source, approvedAt)
    ? { backgroundColor: categoryColor + "22" }
    : { backgroundColor: categoryColor + "66" }
}

export const DocumentTagBadge: React.FC<{
  entry: DocumentTagEntry
  onRemove?: () => void
  onApprove?: () => Promise<void> | void
  onReject?: () => Promise<void> | void
}> = ({ entry, onRemove, onApprove, onReject }) => {
  const { currentLanguage, translate } = useTranslation()
  const [isHovered, setIsHovered] = React.useState(false)
  const [isPending, setIsPending] = React.useState(false)
  const showAutoTagActions =
    onApprove &&
    onReject &&
    isUnapprovedAutoTag(entry.tag.source, entry.tag.approvedAt)

  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsPending(true)
    try {
      await onApprove!()
    } catch {
      // Error feedback is handled by the caller's callback
    } finally {
      setIsPending(false)
    }
  }

  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsPending(true)
    try {
      await onReject!()
    } catch {
      // Error feedback is handled by the caller's callback
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Badge
      variant={getDocumentTagBadgeVariant(
        entry.tag.source,
        entry.tag.approvedAt,
      )}
      className={classNames(
        onRemove || showAutoTagActions ? "inline-flex items-center gap-1" : "",
        getDocumentTagBadgeClassName(entry.tag.source, entry.tag.approvedAt),
      )}
      style={getDocumentTagBadgeStyle(
        entry.tag.source,
        entry.tag.approvedAt,
        entry.category.categoryColor,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {getLocalizedLabel(
        entry.tag.tag.displayNameEn,
        entry.tag.tag.displayNameDe,
        currentLanguage,
      )}
      {showAutoTagActions && (
        <>
          {isPending ? (
            <Loader2 className="ml-0.5 size-2.5 animate-spin opacity-60" />
          ) : isHovered ? (
            <>
              <button
                type="button"
                onClick={(e) => void handleApprove(e)}
                className="ml-0.5 rounded-full text-[green] opacity-80 hover:opacity-100 focus-visible:outline-hidden"
                aria-label={translate("KNOWLEDGEBASE/TAG_APPROVE_ARIA")}
              >
                <Check className="size-3" />
              </button>
              <button
                type="button"
                onClick={(e) => void handleReject(e)}
                className="rounded-full text-destructive opacity-80 hover:opacity-100 focus-visible:outline-hidden"
                aria-label={translate("KNOWLEDGEBASE/TAG_REJECT_ARIA")}
              >
                <X className="size-3" />
              </button>
            </>
          ) : null}
        </>
      )}
      {onRemove && !showAutoTagActions && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 rounded-full opacity-60 hover:opacity-100 focus-visible:outline-hidden"
          aria-label={translate("KNOWLEDGEBASE/TAG_REMOVE_ARIA")}
        >
          <X className="size-2.5" />
        </button>
      )}
    </Badge>
  )
}

export const createDocumentTagMenuEntries = (
  categories: DocumentTagCategoryGroup[],
): DocumentTagEntry[] => {
  return categories.flatMap((category) => {
    const categoryColor = category.categoryColor || DEFAULT_CATEGORY_COLOR
    const tags = category.tags.map(
      (tag): DocumentTagMenuTag => ({
        id: tag.id,
        tagId: tag.tagId,
        tag: tag.tag,
        source: tag.source,
        approvedAt: tag.approvedAt,
        categoryColor,
      }),
    )

    const menuCategory: DocumentTagMenuCategory = {
      categoryId: category.categoryId,
      categoryDisplayNameEn: category.categoryDisplayNameEn,
      categoryColor,
      tags,
    }

    return tags.map((tag) => ({
      category: menuCategory,
      tag,
    }))
  })
}
